import React, { useState, useMemo } from 'react';
import InputTypeSwitch from './InputTypeSwitch';
import IconButton from '../../buttons/IconButton';
import { FieldArray, useFormikContext } from 'formik';
import renderValue from '../utility/renderValue';
import classNames from '../../../utility/classNames';
import Tooltip from '../../Tooltip';

export default function TableInputInline({
  name,
  itemName,
  tableSchema,
  externalData = {},
  label,
  description,
  tooltipHeading,
  tooltipFormula,
}) {
  // Formik populates FieldArray with the array from initialData, so we can access it here through context
  const { values } = useFormikContext();
  const [editIndex, setEditIndex] = useState(null);

  // Extract section values
  const dataArray = values[name] || [];

  const emptyItem = useMemo(
    () =>
      tableSchema.reduce((obj, field) => ({ ...obj, [field.name]: '' }), {}),
    [tableSchema]
  );

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className="w-full grid grid-cols-1 sm:grid-cols-3-inputs">
          {label && (
            <div className="col-span-3 mt-2 mb-4">
              <span className="text-md text-neutral-900 font-medium">
                {label}{' '}
                {description && (
                  <Tooltip
                    text={description}
                    heading={tooltipHeading}
                    formula={tooltipFormula}
                    classes="whitespace-normal"
                    type="info"
                  />
                )}
              </span>
            </div>
          )}
          {Array.isArray(dataArray) &&
            dataArray.map((arrayItem, index) => {
              const cellClasses =
                'px-0 h-full flex items-center py-0 whitespace-nowrap';
              const field = tableSchema[1];
              const arrayName = arrayItem
                ? renderValue(tableSchema[0], arrayItem)
                : null;

              return (
                <React.Fragment key={`${tableSchema[0].name}-${index}`}>
                  <React.Fragment key={field.name}>
                    <div className={classNames(cellClasses, 'mr-2 py-2')}>
                      {index !== editIndex && (
                        <span className="text-md font-normal text-neutral-900">
                          {arrayName || `${itemName} ${index + 1}`}
                          {field.required && (
                            <span className="text-warning" title="required">
                              *
                            </span>
                          )}{' '}
                          {field.description && (
                            <Tooltip
                              text={field.description}
                              heading={field.tooltipHeading}
                              formula={field.tooltipFormula}
                              classes="whitespace-normal"
                              type="info"
                            />
                          )}
                        </span>
                      )}
                      {index === editIndex && (
                        <>
                          <InputTypeSwitch
                            elem={{
                              ...tableSchema[0],
                              name: `${name}.${index}.${tableSchema[0].name}`, // this allows Formik to tell apart the same field in different array items
                              layout: 'tableNoLabel',
                              placeholder: `${itemName} ${index + 1}`,
                              preValue: arrayName
                                ? undefined
                                : `${itemName} #${index + 1}`, // it is important to have the number sign to distinguish from the original table inputs and the ones in before after table
                              emphasize: true,
                            }}
                            placeholder="Name"
                            externalData={values}
                          />
                          {field.required && <span className=" inline">*</span>}
                        </>
                      )}
                    </div>
                    {/* <p
                      className={classNames(
                        'sm:hidden col-span-1 text-base font-medium text-neutral-400 uppercase tracking-wider py-2',
                        cellClasses
                      )}
                    ></p> */}
                    <div
                      className={classNames(
                        cellClasses,
                        'py-2 w-full col-span-3 sm:col-span-2'
                      )}
                    >
                      <InputTypeSwitch
                        elem={{
                          ...field,
                          name: `${name}.${index}.${field.name}`, // this allows Formik to tell apart the same field in different array items
                          layout: 'tableNoLabel',
                        }}
                        externalData={{ ...externalData, ...values }}
                      />
                      <div className="text-right text-base tracking-wider text-neutral-700 pl-2">
                        <IconButton
                          icon="delete"
                          style="modal"
                          classes="focus:outline-none focus:ring-1 focus:ring-warning hover:text-warning focus:text-warning"
                          iconClasses="h-[17px] w-[17px]"
                          handleClick={() => arrayHelpers.remove(index)}
                        />
                        {editIndex !== index && (
                          <IconButton
                            icon="edit"
                            style="modal"
                            iconClasses="h-[17px] w-[17px]"
                            handleClick={() => setEditIndex(index)}
                          />
                        )}
                        {editIndex === index && (
                          <IconButton
                            icon="save"
                            style="modal"
                            iconClasses="h-[17px] w-[17px]"
                            handleClick={() => setEditIndex(null)}
                          />
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                </React.Fragment>
              );
            })}
          <div
            className={classNames(
              'col-span-3',
              dataArray?.length ? 'mt-10' : 'mt-3'
            )}
          >
            <IconButton
              text={`Add ${itemName || 'item'}`}
              icon="plus"
              iconPosition="left"
              classes={'min-w-[14rem]'}
              style="outline"
              handleClick={() => {
                arrayHelpers.push(emptyItem);
                setEditIndex(arrayHelpers.form.values[name].length);
              }}
              iconClasses="text-sm mb-[2px]"
            />
          </div>
        </div>
      )}
    />
  );
}
